import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { login } from '../features/auth/authSlice';
import { Button, GlassCard } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userInfo, loading, error } = useSelector((state) => state.auth);

  const redirect = location.search ? location.search.split('=')[1] : '/';

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [navigate, userInfo, redirect]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await dispatch(login({ email, password })).unwrap();
    } catch (err) {
      setLoginError(err);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 pt-28 pb-16">
      <GlassCard hover={false} className="w-full max-w-md p-6 md:p-8">
        <div className="mb-6 flex justify-center">
          <BrandLogo to="/" className="h-12" />
        </div>
        <h1 className="mb-6 text-2xl font-semibold tracking-tight text-body">{t('auth.login.title')}</h1>
        {error && <Message variant="danger">{error}</Message>}
        {loginError && <Message variant="danger">{loginError}</Message>}
        {loading && <Loader />}
        <form onSubmit={submitHandler} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-muted mb-1">
              {t('auth.login.email')}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t('auth.login.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-muted mb-1">
              {t('auth.login.password')}
            </label>
            <input
              type="password"
              id="password"
              placeholder={t('auth.login.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClassName}
            />
          </div>

          <Button type="submit" className="w-full">
            {t('auth.login.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-muted">
            {t('auth.login.noAccount')}{' '}
            <Link 
              to={redirect ? `/register?redirect=${redirect}` : '/register'}
              className="text-body underline-offset-4 hover:underline"
            >
              {t('auth.login.register')}
            </Link>
          </p>
        </div>
      </GlassCard>
    </div>
  );
};

export default LoginScreen;
